import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import {
  Heart,
  FileDown,
  Trash2,
  Plus,
  Pencil,
  Check,
  GripVertical,
  ArrowUpRight,
} from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useAuth } from '../hooks/useAuth';
import { useFavoriteActions } from '../hooks/useFavoriteActions';
import { useCartActions } from '../hooks/useCartActions';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useFavorites } from '../hooks/useFavorites';
import FavoriteGroupPicker from '../components/common/FavoriteGroupPicker';
import GroupCartPicker from '../components/common/GroupCartPicker';
import DeleteFavoriteGroupDialog from '../components/common/DeleteFavoriteGroupDialog';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import MarketingClaimBadge from '../components/products/MarketingClaimBadge';
import type { FavoriteGroup } from '../hooks/useFavoriteGroups';
import { useTranslation } from '../i18n';
import { useToast } from '../context/ToastContext';

const MAX_GROUPS = 5;

interface DraggableData {
  favoriteId: number;
  fromGroupId: number | null;
}

interface DroppableData {
  groupId: number;
}

function DraggableTile({
  favoriteId,
  fromGroupId,
  isDragging,
  className,
  children,
}: {
  favoriteId: number;
  fromGroupId: number | null;
  isDragging: boolean;
  className: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `fav-${favoriteId}`,
    data: { favoriteId, fromGroupId } satisfies DraggableData,
  });
  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 50,
        position: 'relative',
      }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${className} touch-none select-none ${isDragging ? 'shadow-2xl' : ''}`}
    >
      {children}
    </div>
  );
}

function DroppableGroup({
  groupId,
  className,
  activeClassName,
  children,
}: {
  groupId: number;
  className: string;
  activeClassName: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `group-${groupId}`,
    data: { groupId } satisfies DroppableData,
  });
  return (
    <div ref={setNodeRef} className={`${className} ${isOver ? activeClassName : ''}`}>
      {children}
    </div>
  );
}

const FavoritesPage = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const {
    toggleFavorite,
    favoriteGroups,
    createFavoriteGroup,
    deleteFavoriteGroup,
    renameFavoriteGroup,
    moveFavoriteToGroup,
    addFavoriteToGroup,
  } = useFavoriteActions(isLoggedIn);
  const { addToCart, addAllToCart, removeFromCart, isInCart } = useCartActions(isLoggedIn);
  const { favoriteGroupsEnabled, multiCartEnabled } = useSettingsStore();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: favoritesData = [] } = useFavorites();

  const favoritedProducts = favoritesData.map((f) => ({
    id: f.product_id,
    favoriteId: f.id,
    name: f.product.name,
    articleNumber: f.product.article_number,
    partner: { name: f.product.partner?.name ?? '' },
    category: f.product.category,
    indications: (f.product.indications ?? []).map((i: { name: string }) => i.name),
    marketingClaims: f.product.marketingClaims ?? [],
  }));

  // Drag state
  const [dragFavoriteId, setDragFavoriteId] = useState<number | null>(null);
  const [copyMode, setCopyMode] = useState(false);

  // Sensors: activate after 5px pointer travel. Works for mouse, touch, and
  // pen via the unified Pointer Events API — required for iPad/Android DnD.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const handleDndDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DraggableData | undefined;
    if (!data) return;
    setDragFavoriteId(data.favoriteId);
  };

  const handleDndDragEnd = (event: DragEndEvent) => {
    const activeData = event.active.data.current as DraggableData | undefined;
    const overData = event.over?.data.current as DroppableData | undefined;
    if (activeData && overData) {
      const toGroupId = overData.groupId;
      if (activeData.fromGroupId === null) {
        addFavoriteToGroup(activeData.favoriteId, toGroupId);
      } else if (activeData.fromGroupId !== toGroupId) {
        if (copyMode) {
          addFavoriteToGroup(activeData.favoriteId, toGroupId);
        } else {
          moveFavoriteToGroup(activeData.favoriteId, activeData.fromGroupId, toGroupId);
        }
      }
    }
    setDragFavoriteId(null);
    setCopyMode(false);
  };

  // Track modifier keys during drag for move vs copy
  const handleKeyChange = useCallback((e: KeyboardEvent) => {
    if (dragFavoriteId) {
      setCopyMode(e.metaKey || e.shiftKey);
    }
  }, [dragFavoriteId]);

  useEffect(() => {
    if (!dragFavoriteId) return;
    window.addEventListener('keydown', handleKeyChange);
    window.addEventListener('keyup', handleKeyChange);
    return () => {
      window.removeEventListener('keydown', handleKeyChange);
      window.removeEventListener('keyup', handleKeyChange);
    };
  }, [dragFavoriteId, handleKeyChange]);

  // Rename state
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  // New group input
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Delete group dialog
  const [deletingGroup, setDeletingGroup] = useState<FavoriteGroup | null>(null);

  const startRename = (groupId: number, currentName: string) => {
    setEditingGroupId(groupId);
    setEditName(currentName);
  };

  const saveRename = () => {
    if (editingGroupId && editName.trim()) {
      renameFavoriteGroup(editingGroupId, editName.trim());
    }
    setEditingGroupId(null);
  };

  const handleCreateGroup = () => {
    const trimmed = newGroupName.trim();
    if (trimmed) {
      createFavoriteGroup(trimmed);
      setNewGroupName('');
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = (groupId: number | string, moveToGroupId: number | string | null) => {
    const group = favoriteGroups.find((g) => g.id === groupId);
    if (group && moveToGroupId) {
      for (const fav of group.favorites ?? []) {
        moveFavoriteToGroup(fav.id, groupId, moveToGroupId);
      }
    }
    deleteFavoriteGroup(groupId);
    setDeletingGroup(null);
  };

  /* ------------------------------------------------------------------ */
  /* Empty state                                                         */
  /* ------------------------------------------------------------------ */
  if (favoritedProducts.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <Heart className="w-7 h-7 text-red-500" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.favorites.title}
          </h1>
        </div>

        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Heart className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t.favorites.noFavorites}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md">
            {t.favorites.noFavoritesDescription}
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 rounded-full bg-vivatis-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-vivatis-700 transition-colors"
          >
            {t.favorites.browseProducts}
          </Link>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Groups view                                                         */
  /* ------------------------------------------------------------------ */
  if (favoriteGroupsEnabled) {
    return (
      <DndContext
        sensors={sensors}
        onDragStart={handleDndDragStart}
        onDragEnd={handleDndDragEnd}
      >
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <Heart className="w-7 h-7 text-red-500" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.favorites.title}
          </h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({favoritedProducts.length})
          </span>
        </div>

        {/* Drag hint */}
        {dragFavoriteId && (
          <div className="text-xs text-center text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 rounded-lg px-3 py-2 transition-all">
            {copyMode
              ? t.favorites.dragCopyHint
              : t.favorites.dragMoveHint}
          </div>
        )}

        {/* Groups */}
        <div className="space-y-6">
          {favoriteGroups.map((group) => {
            const groupFavorites = group.favorites ?? [];
            const groupProducts = groupFavorites.map((f) => ({
              id: f.product_id,
              favoriteId: f.id,
              name: f.product.name,
              articleNumber: f.product.article_number,
              partner: { name: f.product.partner?.name ?? '' },
              indications: [] as string[],
              marketingClaims: [] as { id: number; name: string; icon?: string | null; color?: string | null }[],
            }));

            return (
              <DroppableGroup
                key={group.id}
                groupId={Number(group.id)}
                className="rounded-2xl border-2 transition-colors border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50"
                activeClassName="!border-vivatis-400 !bg-vivatis-50/50 dark:!bg-vivatis-950/20"
              >
                {/* Group header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {editingGroupId === group.id ? (
                      <form
                        className="flex items-center gap-2 flex-1"
                        onSubmit={(e) => { e.preventDefault(); saveRename(); }}
                      >
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 text-sm font-semibold text-anthracite-500 dark:text-gray-100 bg-transparent border-b-2 border-vivatis-600 focus:outline-none"
                          autoFocus
                          onBlur={saveRename}
                        />
                        <button type="submit" className="p-1 text-vivatis-600">
                          <Check className="w-4 h-4" />
                        </button>
                      </form>
                    ) : (
                      <>
                        <h3 className="text-sm font-semibold text-anthracite-500 dark:text-gray-100 truncate">
                          {group.name}
                        </h3>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          ({groupProducts.length})
                        </span>
                        <button
                          type="button"
                          onClick={() => startRename(Number(group.id), group.name)}
                          className="p-1 rounded text-gray-400 hover:text-vivatis-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {groupProducts.length > 0 && (
                      multiCartEnabled ? (
                        <GroupCartPicker productIds={groupFavorites.map((f) => f.product_id)} />
                      ) : (
                        (() => {
                          const ids = groupFavorites.map((f) => f.product_id);
                          const allInDocument = ids.length > 0 && ids.every((id) => isInCart(id));
                          return (
                            <Button
                              variant={allInDocument ? 'secondary' : 'primary'}
                              size="sm"
                              icon={<FileDown className="w-3.5 h-3.5" />}
                              title={allInDocument ? t.actions.removeFromAllCarts : t.favorites.addGroupToDocument}
                              aria-label={allInDocument ? t.actions.removeFromAllCarts : t.favorites.addGroupToDocument}
                              onClick={() => {
                                if (allInDocument) {
                                  ids.forEach((id) => removeFromCart(id));
                                  toast.show(t.favorites.removedFromDocument, 'success');
                                  return;
                                }
                                addAllToCart(ids);
                                toast.show(t.favorites.addedToDocument, 'success');
                              }}
                            >
                              <span className="hidden sm:inline">
                                {allInDocument ? t.actions.removeFromAllCarts : t.favorites.addGroupToDocument}
                              </span>
                            </Button>
                          );
                        })()
                      )
                    )}
                    {favoriteGroups.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setDeletingGroup(group)}
                        className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title={t.favorites.deleteGroupTitle}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Group products */}
                {groupProducts.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                    {t.favorites.dragProductsHint}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3">
                    {groupProducts.map((product) => {
                      if (!product) return null;
                      const inCart = isInCart(product.id);

                      return (
                        <DraggableTile
                          key={product.id}
                          favoriteId={Number(product.favoriteId)}
                          fromGroupId={Number(group.id)}
                          isDragging={dragFavoriteId === Number(product.favoriteId)}
                          className="group relative flex flex-col bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors duration-150 cursor-grab active:cursor-grabbing"
                        >
                          {/* Drag handle affordance (non-interactive — the whole card is draggable) */}
                          <div className="absolute top-1.5 left-1.5 z-10 p-1 rounded text-gray-400 pointer-events-none">
                            <GripVertical className="w-4 h-4" />
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 p-4 pl-8 gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm">
                                {product.name}
                              </h3>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {product.partner.name}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                              {product.articleNumber}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {product.indications.map((ind) => (
                                <Badge key={ind} variant="default">
                                  {ind}
                                </Badge>
                              ))}
                            </div>
                            {product.marketingClaims.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                                {product.marketingClaims.map((claim) => (
                                  <MarketingClaimBadge key={claim.id ?? claim.name} claim={claim} />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Action bar */}
                          <div
                            className="flex items-center justify-between bg-gray-100/50 dark:bg-gray-700/50 px-4 py-2 rounded-b-xl"
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <FavoriteGroupPicker
                              productId={String(product.id)}
                              onSimpleToggle={(e) => { e.stopPropagation(); toggleFavorite(String(product.id)); }}
                            />

                            <div className="flex items-center gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={<ArrowUpRight className="w-3.5 h-3.5" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/${product.id}`, { state: { backgroundLocation: location } });
                                }}
                                draggable={false}
                              >
                                {t.favorites.openProductTitle}
                              </Button>
                              <AnimatedCartButton
                                inCart={inCart}
                                onClick={(e) => { e.stopPropagation(); addToCart(product.id); }}
                              />
                            </div>
                          </div>
                        </DraggableTile>
                      );
                    })}
                  </div>
                )}
              </DroppableGroup>
            );
          })}
        </div>

        {/* Ungrouped favorites */}
        {(() => {
          const groupedProductIds = new Set(
            favoriteGroups.flatMap((g) => (g.favorites ?? []).map((f) => f.product_id))
          );
          const ungrouped = favoritedProducts.filter((p) => !groupedProductIds.has(p.id));
          if (ungrouped.length === 0) return null;
          return (
            <div className="rounded-xl bg-gray-100/50 dark:bg-gray-800/30 border border-dashed border-gray-300 dark:border-gray-600 p-5">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                {t.favorites.notInGroupWithCount(ungrouped.length)}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {ungrouped.map((product) => {
                  const inCart = isInCart(product.id);
                  return (
                    <DraggableTile
                      key={product.id}
                      favoriteId={Number(product.favoriteId)}
                      fromGroupId={null}
                      isDragging={dragFavoriteId === Number(product.favoriteId)}
                      className="group relative flex flex-col bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors duration-150 cursor-grab active:cursor-grabbing"
                    >
                      {/* Drag handle affordance (non-interactive — the whole card is draggable) */}
                      <div className="absolute top-1.5 left-1.5 z-10 p-1 rounded text-gray-400 pointer-events-none">
                        <GripVertical className="w-4 h-4" />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col flex-1 p-4 pl-8 gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm">
                            {product.name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {product.partner.name}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                          {product.articleNumber}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {product.indications.map((ind) => (
                            <Badge key={ind} variant="default">{ind}</Badge>
                          ))}
                        </div>
                        {product.marketingClaims.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                            {product.marketingClaims.map((claim) => (
                              <MarketingClaimBadge key={claim.id ?? claim.name} claim={claim} />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action bar */}
                      <div
                        className="flex items-center justify-between bg-gray-100/50 dark:bg-gray-700/50 px-4 py-2 rounded-b-xl"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <FavoriteGroupPicker
                          productId={String(product.id)}
                          onSimpleToggle={(e) => { e.stopPropagation(); toggleFavorite(String(product.id)); }}
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<ArrowUpRight className="w-3.5 h-3.5" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/products/${product.id}`, { state: { backgroundLocation: location } });
                            }}
                            draggable={false}
                          >
                            {t.favorites.openProductTitle}
                          </Button>
                          <AnimatedCartButton
                            inCart={inCart}
                            onClick={(e) => { e.stopPropagation(); addToCart(product.id); }}
                          />
                        </div>
                      </div>
                    </DraggableTile>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Add group button */}
        {favoriteGroups.length < MAX_GROUPS && (
          <div>
            {creatingGroup ? (
              <form
                className="flex items-center gap-2 max-w-xs"
                onSubmit={(e) => { e.preventDefault(); handleCreateGroup(); }}
              >
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t.favorites.groupNamePlaceholder}
                  className="flex-1 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-vivatis-500"
                  autoFocus
                />
                <Button variant="primary" size="sm" onClick={handleCreateGroup}>
                  {t.favorites.create}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setCreatingGroup(false); setNewGroupName(''); }}>
                  {t.favorites.cancel}
                </Button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setCreatingGroup(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-vivatis-400 hover:text-vivatis-600 dark:hover:text-vivatis-400 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t.favorites.newGroup}
              </button>
            )}
          </div>
        )}

        {/* Delete group confirmation dialog */}
        <DeleteFavoriteGroupDialog
          isOpen={deletingGroup !== null}
          group={deletingGroup}
          allGroups={favoriteGroups}
          onConfirm={handleDeleteGroup}
          onCancel={() => setDeletingGroup(null)}
        />
      </div>
      </DndContext>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Simple flat list (no groups)                                        */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Heart className="w-7 h-7 text-red-500" />
        <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
          Favorites
        </h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          ({favoritedProducts.length})
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {favoritedProducts.map((product) => {
          const inCart = isInCart(product.id);

          return (
            <div
              key={product.id}
              onClick={() => navigate(`/products/${product.id}`, { state: { backgroundLocation: location } })}
              className="group relative flex flex-col bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors duration-150 cursor-pointer overflow-hidden"
            >
              {/* Content */}
              <div className="flex flex-col flex-1 p-4 gap-2">
                {/* Name & Partner */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm">
                    {product.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {product.partner.name}
                  </p>
                </div>

                {/* Article number */}
                <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                  {product.articleNumber}
                </p>

                {/* Indication tags */}
                <div className="flex flex-wrap gap-1">
                  {product.indications.map((ind) => (
                    <Badge key={ind} variant="default">
                      {ind}
                    </Badge>
                  ))}
                </div>

                {/* Marketing claim icons */}
                {product.marketingClaims.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                    {product.marketingClaims.map((claim) => (
                      <MarketingClaimBadge key={claim.id ?? claim.name} claim={claim} />
                    ))}
                  </div>
                )}
              </div>

              {/* Action bar */}
              <div className="flex items-center justify-between bg-gray-100/50 dark:bg-gray-700/50 px-4 py-2">
                <FavoriteGroupPicker
                  productId={String(product.id)}
                  onSimpleToggle={(e) => { e.stopPropagation(); toggleFavorite(String(product.id)); }}
                />

                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ArrowUpRight className="w-3.5 h-3.5" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/products/${product.id}`, { state: { backgroundLocation: location } });
                    }}
                  >
                    {t.favorites.openProductTitle}
                  </Button>
                  <AnimatedCartButton
                    inCart={inCart}
                    onClick={(e) => { e.stopPropagation(); addToCart(product.id); }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function AnimatedCartButton({ inCart, onClick }: { inCart: boolean; onClick: (e: React.MouseEvent) => void }) {
  const { t } = useTranslation();
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleClick = (e: React.MouseEvent) => {
    onClick(e);
    setAnimating(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setAnimating(false), 400);
  };

  const style: React.CSSProperties = {
    ...(animating ? { animation: 'action-pop 0.4s ease-out' } : {}),
    // When in-cart, the button is visually disabled but must let pointer
    // events pass through so the draggable parent tile still receives
    // mousedown and can start an HTML5 drag. Native `disabled` would
    // swallow mousedown in the button area and break drag-and-drop.
    ...(inCart ? { pointerEvents: 'none' } : {}),
  };

  return (
    <Button
      variant={inCart ? 'secondary' : 'primary'}
      size="sm"
      icon={<FileDown className="w-3.5 h-3.5" />}
      onClick={handleClick}
      aria-disabled={inCart}
      style={style}
    >
      {inCart ? t.favorites.inDocument : t.favorites.addToDocument}
    </Button>
  );
}

export default FavoritesPage;
